import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  // Card,
  // CardContent,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  // ListItemIcon,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/Warning';
import UpdateIcon from '@mui/icons-material/Update';
import LockIcon from '@mui/icons-material/Lock';
import KeyIcon from '@mui/icons-material/Key';
import CloudIcon from '@mui/icons-material/Cloud';
import api from '../../../services/api';

const SystemInfo = () => {
  const [loading, setLoading] = useState(true);
  const [systemInfo, setSystemInfo] = useState({
    version: '1.0.0',
    buildNumber: '20250509-001',
    environment: 'production',
    nodeVersion: '18.16.0',
    databaseType: 'PostgreSQL',
    databaseVersion: '14.5',
    uptime: 0,
    lastRestart: new Date().toISOString(),
    license: {
      type: 'Enterprise',
      validUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      maxUsers: 100,
      maxProjects: 500,
      features: [
        'User Management',
        'Role-Based Access Control',
        'Project Templates',
        'Advanced Reporting',
        'API Integration',
        'Custom Branding',
        'Audit Logs',
        'Testing & Simulation'
      ],
      isValid: true
    }
  });
  
  const [licenseKey, setLicenseKey] = useState('');
  const [updating, setUpdating] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [updateInfo, setUpdateInfo] = useState({
    available: true,
    version: '1.1.0',
    releaseDate: new Date().toISOString(),
    releaseNotes: '- Peningkatan performa\n- Perbaikan bug pada modul laporan\n- Fitur baru: ekspor data ke Excel\n- Peningkatan keamanan'
  });
  
  // Menggunakan useCallback untuk memastikan fungsi fetchSystemInfo stabil antar render
  const fetchSystemInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/system/info').catch(() => {
        // If API endpoint doesn't exist yet, use default settings
        return { data: { info: systemInfo } };
      });
      
      setSystemInfo(response.data.info);
    } catch (error) {
      console.error('Error fetching system info:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat informasi sistem',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [systemInfo, setAlert]); // Tambahkan dependencies yang digunakan di dalam callback
  
  useEffect(() => {
    fetchSystemInfo();
  }, [fetchSystemInfo]); // Sekarang fetchSystemInfo stabil antar render
  
  const handleLicenseKeyChange = (e) => {
    setLicenseKey(e.target.value);
  };
  
  const handleUpdateLicense = async () => {
    if (!licenseKey.trim()) {
      setAlert({
        open: true,
        message: 'Masukkan kunci lisensi yang valid',
        severity: 'error'
      });
      return;
    }
    
    setUpdating(true);
    
    try {
      await api.post('/admin/system/license', { licenseKey }).catch(() => {
        // If API endpoint doesn't exist yet, just simulate success
      });
      
      // Update license info with new expiry date
      setSystemInfo(prev => ({
        ...prev,
        license: {
          ...prev.license,
          validUntil: new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toISOString(), // 2 years
          isValid: true
        }
      }));
      
      setLicenseKey('');
      
      setAlert({
        open: true,
        message: 'Lisensi berhasil diperbarui',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating license:', error);
      setAlert({
        open: true,
        message: 'Gagal memperbarui lisensi',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };
  
  const handleCheckForUpdates = async () => {
    setUpdating(true);
    
    try {
      const response = await api.get('/admin/system/updates').catch(() => {
        // If API endpoint doesn't exist yet, use mock data
        return { data: { update: updateInfo } };
      });
      
      setUpdateInfo(response.data.update);
      
      if (response.data.update.available) {
        setOpenUpdateDialog(true);
      } else {
        setAlert({
          open: true,
          message: 'Sistem sudah menggunakan versi terbaru',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
      setAlert({
        open: true,
        message: 'Gagal memeriksa pembaruan',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };
  
  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
  };
  
  const handleInstallUpdate = async () => {
    setUpdating(true);
    
    try {
      await api.post('/admin/system/update').catch(() => {
        // If API endpoint doesn't exist yet, just simulate success
      });
      
      setOpenUpdateDialog(false);
      
      setAlert({
        open: true,
        message: 'Pembaruan berhasil diinstal. Sistem akan restart dalam beberapa saat.',
        severity: 'success'
      });
      
      // Update version info
      setSystemInfo(prev => ({
        ...prev,
        version: updateInfo.version,
        buildNumber: `${updateInfo.version}-${new Date().toISOString().split('T')[0]}`
      }));
    } catch (error) {
      console.error('Error installing update:', error);
      setAlert({
        open: true,
        message: 'Gagal menginstal pembaruan',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };
  
  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };
  
  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    return `${days} hari, ${hours} jam, ${minutes} menit`;
  };
  
  const getLicenseStatusColor = () => {
    if (!systemInfo.license.isValid) {
      return 'error';
    }
    
    const validUntil = new Date(systemInfo.license.validUntil);
    const now = new Date();
    const daysLeft = Math.floor((validUntil - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 30) {
      return 'warning';
    }
    
    return 'success';
  };
  
  const getLicenseStatusText = () => {
    if (!systemInfo.license.isValid) {
      return 'Tidak Valid';
    }
    
    const validUntil = new Date(systemInfo.license.validUntil);
    const now = new Date();
    const daysLeft = Math.floor((validUntil - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) {
      return 'Kedaluwarsa';
    }
    
    if (daysLeft <= 30) {
      return `Akan Kedaluwarsa dalam ${daysLeft} hari`;
    }
    
    return 'Aktif';
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Informasi Sistem
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Lihat informasi tentang sistem dan lisensi
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InfoIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Informasi Sistem
              </Typography>
            </Box>
            
            <List>
              <ListItem>
                <ListItemText
                  primary="Versi Aplikasi"
                  secondary={systemInfo.version}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Build Number"
                  secondary={systemInfo.buildNumber}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Environment"
                  secondary={systemInfo.environment}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Node.js Version"
                  secondary={systemInfo.nodeVersion}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Database"
                  secondary={`${systemInfo.databaseType} ${systemInfo.databaseVersion}`}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Uptime"
                  secondary={formatUptime(systemInfo.uptime)}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Last Restart"
                  secondary={new Date(systemInfo.lastRestart).toLocaleString()}
                />
              </ListItem>
            </List>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<UpdateIcon />}
                onClick={handleCheckForUpdates}
                disabled={updating}
              >
                {updating ? <CircularProgress size={24} /> : 'Periksa Pembaruan'}
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LockIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Informasi Lisensi
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <Chip
                icon={systemInfo.license.isValid ? <VerifiedIcon /> : <WarningIcon />}
                label={getLicenseStatusText()}
                color={getLicenseStatusColor()}
                sx={{ mr: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {systemInfo.license.isValid ? (
                  `Valid hingga: ${new Date(systemInfo.license.validUntil).toLocaleDateString()}`
                ) : (
                  'Lisensi tidak valid atau kedaluwarsa'
                )}
              </Typography>
            </Box>
            
            <List>
              <ListItem>
                <ListItemText
                  primary="Tipe Lisensi"
                  secondary={systemInfo.license.type}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Maksimum Pengguna"
                  secondary={systemInfo.license.maxUsers}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Maksimum Proyek"
                  secondary={systemInfo.license.maxProjects}
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Fitur Terlisensi"
                  secondary={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {systemInfo.license.features.map((feature, index) => (
                        <Chip
                          key={index}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  }
                />
              </ListItem>
            </List>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Perbarui Lisensi
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Kunci Lisensi"
                  value={licenseKey}
                  onChange={handleLicenseKeyChange}
                  margin="normal"
                  placeholder="Masukkan kunci lisensi baru"
                  InputProps={{
                    startAdornment: <KeyIcon color="action" sx={{ mr: 1 }} />
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleUpdateLicense}
                  disabled={updating || !licenseKey.trim()}
                  sx={{ mt: 2 }}
                >
                  {updating ? <CircularProgress size={24} /> : 'Perbarui'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CloudIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Dukungan & Bantuan
              </Typography>
            </Box>
            
            <Typography variant="body2" paragraph>
              Jika Anda memerlukan bantuan atau dukungan teknis, silakan hubungi tim dukungan kami:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemText
                  primary="Email Dukungan"
                  secondary="support@hakikiproject.com"
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Telepon Dukungan"
                  secondary="+62 21 1234 5678"
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Jam Dukungan"
                  secondary="Senin - Jumat, 08:00 - 17:00 WIB"
                />
              </ListItem>
              <Divider component="li" />
              
              <ListItem>
                <ListItemText
                  primary="Portal Dukungan"
                  secondary="https://support.hakikiproject.com"
                />
              </ListItem>
            </List>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => window.open('https://support.hakikiproject.com', '_blank')}
              >
                Buka Portal Dukungan
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      <Dialog
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Pembaruan Tersedia
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Versi {updateInfo.version} tersedia untuk diinstal
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Tanggal Rilis: {new Date(updateInfo.releaseDate).toLocaleDateString()}
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Catatan Rilis:
          </Typography>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            {updateInfo.releaseNotes}
          </Typography>
          
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            <WarningIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Pastikan untuk membuat backup data sebelum melakukan pembaruan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>
            Batalkan
          </Button>
          <Button 
            variant="contained"
            onClick={handleInstallUpdate}
            disabled={updating}
          >
            {updating ? <CircularProgress size={24} /> : 'Instal Pembaruan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemInfo;
